import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  LayoutDashboard,
  ListChecks,
  Sparkles,
  Trophy,
} from 'lucide-react';
import React from 'react';

export type CompareBullet = { bold: string; rest: string };
export type CompareRow = { feature: string; them: boolean; us: boolean };

export type CompetitorStep = {
  id: string;
  icon: React.ReactNode;
  title: string;
  titleAccent: string;
  competitor: string;
  ggjPitch: string;
  desc: string;
  bullets: CompareBullet[];
  rows: CompareRow[];
};

const STEP_ICONS: Record<string, React.ReactNode> = {
  'resume-tools': React.createElement(FileText, { className: 'h-4 w-4' }),
  'ai-tools': React.createElement(Sparkles, { className: 'h-4 w-4' }),
  'job-boards': React.createElement(LayoutDashboard, { className: 'h-4 w-4' }),
  'prep-apps': React.createElement(ListChecks, { className: 'h-4 w-4' }),
  'full-loop': React.createElement(Trophy, { className: 'h-4 w-4' }),
};

const STEP_IDS = ['resume-tools', 'ai-tools', 'job-boards', 'prep-apps', 'full-loop'] as const;

export function useCompetitorCompareSteps(): CompetitorStep[] {
  const { t, i18n } = useTranslation();

  return useMemo(
    () =>
      STEP_IDS.map((id) => {
        const base = `home.competitorCompare.steps.${id}`;
        const bullets = t(`${base}.bullets`, { returnObjects: true, defaultValue: [] }) as CompareBullet[];
        const rows = t(`${base}.rows`, { returnObjects: true, defaultValue: [] }) as CompareRow[];

        return {
          id,
          icon: STEP_ICONS[id],
          title: t(`${base}.title`, { defaultValue: '' }),
          titleAccent: t(`${base}.titleAccent`, { defaultValue: '' }),
          competitor: t(`${base}.competitor`, { defaultValue: '' }),
          ggjPitch: t(`${base}.ggjPitch`, { defaultValue: '' }),
          desc: t(`${base}.desc`, { defaultValue: '' }),
          bullets: Array.isArray(bullets) ? bullets : [],
          rows: Array.isArray(rows) ? rows : [],
        };
      }),
    [t, i18n.language],
  );
}

export function useCompetitorCompareLabels() {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => ({
      comparing: t('home.competitorCompare.labels.comparing', { defaultValue: 'Comparing' }),
      vs: t('home.competitorCompare.labels.vs', { defaultValue: 'vs' }),
      vsOthers: t('home.competitorCompare.labels.vsOthers', { defaultValue: 'vs Others' }),
      ggj: t('home.competitorCompare.labels.ggj', { defaultValue: 'GoGrabJob' }),
      addsCapabilities: (count: number) =>
        t('home.competitorCompare.labels.addsCapabilities', {
          defaultValue: "GoGrabJob adds {{count}} capabilities others don't offer for this step.",
          count,
        }),
      whatOthersOffer: t('home.competitorCompare.labels.whatOthersOffer', {
        defaultValue: 'What others offer',
      }),
      whatGgjOffers: t('home.competitorCompare.labels.whatGgjOffers', {
        defaultValue: 'What GoGrabJob offers',
      }),
      limited: t('home.competitorCompare.labels.limited', { defaultValue: 'Limited' }),
      youGetThis: t('home.competitorCompare.labels.youGetThis', { defaultValue: 'You get this' }),
      capability: t('home.competitorCompare.labels.capability', { defaultValue: 'Capability' }),
      othersCol: t('home.competitorCompare.labels.othersCol', { defaultValue: 'Others' }),
      ggjCol: t('home.competitorCompare.labels.ggjCol', { defaultValue: 'GoGrabJob' }),
      whereOthersFallShort: t('home.competitorCompare.labels.whereOthersFallShort', {
        defaultValue: 'Where others fall short',
      }),
      whatGgjGivesYou: t('home.competitorCompare.labels.whatGgjGivesYou', {
        defaultValue: 'What GoGrabJob gives you',
      }),
      seeComparison: t('home.competitorCompare.labels.seeComparison', {
        defaultValue: 'See side-by-side comparison in the preview →',
      }),
      fullLoopCompareThem: t('home.competitorCompare.labels.fullLoopCompareThem', {
        defaultValue: 'Resume tools · AI · Boards · Prep',
      }),
      fullLoopCompareUs: t('home.competitorCompare.labels.fullLoopCompareUs', {
        defaultValue: 'GoGrabJob full loop',
      }),
      fullLoopCallout: t('home.competitorCompare.labels.fullLoopCallout', {
        defaultValue:
          'Each competitor owns one slice. GoGrabJob covers Discover → Optimize → Track → Prep.',
      }),
    }),
    [t, i18n.language],
  );
}

export function useCompetitorGgjStrengths(): Record<string, string[]> {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const id of ['resume-tools', 'ai-tools', 'job-boards', 'prep-apps']) {
      const items = t(`home.competitorCompare.ggjStrengths.${id}`, {
        returnObjects: true,
        defaultValue: [],
      }) as string[];
      out[id] = Array.isArray(items) ? items : [];
    }
    return out;
  }, [t, i18n.language]);
}
